// src/pages/auth/Register.js
import { useState } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";

export default function Register() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState("Athlete");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (role === "SkillsTrainer") {
      setError("Skills Trainer accounts must be created by an administrator.");
      return;
    }

    try {
      setLoading(true);

      // 1. Sign Up with Metadata
      // We pass both role and full_name so the Database Trigger can use them.
      const { error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role: role,
            full_name: fullName, // Passing the name here
          },
        },
      });

      if (authError) throw authError;

      // 2. Success Message & Redirect
      // We now redirect everyone to Login, regardless of session status.
      setMessage("Account created successfully!");
      setTimeout(() => navigate("/login"), 3000);

    } catch (err) {
      console.error("Registration Error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-[100svh] min-h-screen w-full flex items-center justify-center font-sans pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">

      {/* FULL PAGE BACKGROUND */}
      <div
        className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1526676037777-05a232554f77?q=80&w=2070&auto=format&fit=crop')`, 
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-blue-900/40 backdrop-blur-[4px]"></div>
      </div>

      {/* REGISTER CARD */}
      <div className="relative z-10 w-full max-w-[480px] px-4 py-8">
        <div className="bg-white/10 backdrop-blur-2xl border border-white/20 p-8 md:p-12 rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.3)] max-h-[90svh] overflow-y-auto">

          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-block bg-blue-600 text-white text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1 rounded-full mb-4">
              New Account
            </div>
            <h1 className="text-4xl font-black text-white tracking-tighter italic uppercase">
              Nuvio<span className="text-blue-500">Sport</span>
            </h1>
            <p className="text-gray-400 text-xs mt-2 uppercase tracking-widest font-bold">Start your performance journey</p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-2xl text-xs mb-6 font-bold flex items-center gap-2">
              ⚠️ {error}
            </div>
          )}

          {message && (
            <div className="bg-green-500/10 border border-green-500/50 text-green-400 p-4 rounded-2xl text-xs mb-6 font-bold">
              ✅ {message}
            </div>
          )}

          <form onSubmit={handleRegister} className="space-y-5">
            {/* Role Selector */}
            <div className="space-y-2">
              <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-1">Select Your Role</label>
              <div className="relative">
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-2xl p-4 text-white appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all cursor-pointer font-bold"
                >
                  <option value="Athlete" className="bg-gray-900 text-white">Athlete</option>
                  <option value="Coach" className="bg-gray-900 text-white">Coach</option>
                  <option value="Administrator" className="bg-gray-900 text-white">Program Director</option>
                </select>
                <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-gray-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                </div>
              </div>
            </div>

            {/* Full Name Input */}
            <div className="space-y-2">
              <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-1">Full Name</label>
              <input
                type="text"
                placeholder="John Doe"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full bg-black/20 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-500 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                required
              />
            </div>

            {/* Email Input */}
            <div className="space-y-2">
              <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-1">Email Address</label>
              <input
                type="email"
                placeholder="athlete@performance.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-black/20 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-500 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                required
              />
            </div>

            {/* Password Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-1">Password</label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-black/20 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-500 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest ml-1">Confirm</label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-black/20 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-500 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-white hover:bg-blue-600 text-black hover:text-white font-black py-4 rounded-2xl transition-colors duration-200 active:scale-[0.98] disabled:opacity-50 mt-4 uppercase tracking-widest shadow-lg shadow-black/50"
            >
              {loading ? "Registering..." : "Create Account"}
            </button>

            <div className="pt-6 text-center">
              <p className="text-gray-400 text-xs font-medium">
                Already part of the program?{" "}
                <button
                  type="button"
                  className="text-white hover:text-blue-400 font-black transition underline decoration-blue-500 decoration-2 underline-offset-4"
                  onClick={() => navigate("/login")}
                >
                  Sign In
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
